import React, { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  Flex,
  Badge,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import 'moment/locale/pt-br';
import { useCompanies } from '../../../../../hooks/useCompanies';
import type { ITaxRegime, IMonthlyFaturamento } from '../../../../../hooks/useCompanies';
import { formatCNPJ, toCurrency } from '../../../../../utils/FormatFunctions';

interface TaxRegimeDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: ITaxRegime | null;
  currentYear: number;
  socioId?: string;
}

const LIMITE_SIMPLES_NACIONAL = 4800000;
const LIMITE_LUCRO_PRESUMIDO = 78000000;

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export function TaxRegimeDetailModal({
  isOpen,
  onClose,
  company,
  currentYear,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  socioId,
}: TaxRegimeDetailModalProps) {
  const { getMonthlyFaturamento } = useCompanies();

  // Determinar o regime real da empresa para usar em toda a lógica
  const regimeEmpresa = company?.regime_tributario?.toLowerCase() || '';
  const isSimplesNacional = regimeEmpresa.includes('simples nacional');
  const isLucroPresumido = regimeEmpresa.includes('lucro presumido');

  // Determinar o limite baseado no regime real da empresa, não no filtro
  let limite = LIMITE_SIMPLES_NACIONAL; // Default para Simples Nacional
  if (isLucroPresumido) {
    limite = LIMITE_LUCRO_PRESUMIDO;
  } else if (isSimplesNacional) {
    limite = LIMITE_SIMPLES_NACIONAL;
  }

  // Buscar dados mensais usando o novo endpoint otimizado
  const { data: monthlyData, isFetching } = useQuery<IMonthlyFaturamento[]>({
    queryKey: [
      'taxRegimeMonthlyFaturamento',
      { external_id: company?.external_id, year: currentYear },
    ],
    queryFn: getMonthlyFaturamento,
    enabled: isOpen && !!company?.external_id,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  const chartData = useMemo(() => {
    if (!monthlyData || monthlyData.length === 0) return [];
    return monthlyData.map(item => ({
      mes: item.mes,
      mesLabel: MESES[item.mes - 1],
      faturamento: item.faturamento,
      acumulado: item.acumulado,
      limite,
    }));
  }, [monthlyData, limite]);

  if (!company) return null;

  const faturamento = company.faturamento_acumulado || 0;
  const limiteRestante = limite - faturamento;
  const percentualAtingido = company.limite_atingido || 0;

  const getStatusColor = () => {
    if (percentualAtingido >= 80) return 'red';
    if (percentualAtingido >= 60) return 'orange';
    if (percentualAtingido >= 40) return 'yellow';
    return 'green';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          <Flex align="center" gap={3}>
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                {company.razao_social}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {formatCNPJ(company.cnpj)} | ID: {company.external_id}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <Flex align="center" gap={2} mb={4} flexWrap="wrap">
            <Badge colorScheme={getStatusColor()} fontSize="sm" px={2} py={1}>
              {percentualAtingido}% do limite atingido
            </Badge>
            <Badge
              colorScheme={isSimplesNacional ? 'purple' : 'orange'}
              fontSize="sm"
              px={2}
              py={1}
            >
              {(() => {
                if (isSimplesNacional) return 'Simples Nacional';
                if (isLucroPresumido) return 'Lucro Presumido';
                return 'N/A';
              })()}
            </Badge>
            {isSimplesNacional &&
              company.regime_tributario &&
              company.regime_tributario.replace(/^simples\s+nacional\s*-\s*/i, '').trim() && (
                <Badge colorScheme="teal" fontSize="sm" px={2} py={1} maxW="200px">
                  <Text noOfLines={1} title={company.regime_tributario}>
                    {company.regime_tributario.replace(/^simples\s+nacional\s*-\s*/i, '')}
                  </Text>
                </Badge>
              )}
            <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
              Ano: {currentYear}
            </Badge>
          </Flex>

          <Divider mb={4} />

          <SimpleGrid columns={3} spacing={4} mb={6}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">
                Faturamento Acumulado Total
              </StatLabel>
              <StatNumber fontSize="md" color="blue.600">
                {toCurrency(faturamento)}
              </StatNumber>
              <StatHelpText fontSize="xs">No ano {currentYear}</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">
                Limite do Regime
              </StatLabel>
              <StatNumber fontSize="md" color="gray.700">
                {toCurrency(limite)}
              </StatNumber>
              <StatHelpText fontSize="xs">Anual</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">
                Limite Restante
              </StatLabel>
              <StatNumber fontSize="md" color={limiteRestante > 0 ? 'green.500' : 'red.500'}>
                {toCurrency(limiteRestante > 0 ? limiteRestante : 0)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                {limiteRestante > 0 ? 'Disponivel' : 'Excedido'}
              </StatHelpText>
            </Stat>
          </SimpleGrid>

          <Box bg="gray.50" p={4} borderRadius="lg">
            <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">
              Faturamento Mensal e Acumulado - {currentYear}
            </Text>

            {isFetching ? (
              <Flex justify="center" align="center" h="250px">
                <Spinner size="lg" color="blue.500" />
              </Flex>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mesLabel" fontSize={11} />
                  <YAxis
                    tickFormatter={value => {
                      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                      return value;
                    }}
                    fontSize={10}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      let label = name;
                      if (name === 'faturamento') {
                        label = 'Faturamento Mensal';
                      } else if (name === 'acumulado') {
                        label = 'Acumulado';
                      }
                      return [toCurrency(value), label];
                    }}
                    labelFormatter={label => `Mês: ${label}`}
                  />
                  <Legend
                    formatter={(value: string) => {
                      if (value === 'faturamento') return 'Faturamento Mensal';
                      if (value === 'acumulado') return 'Acumulado';
                      return value;
                    }}
                  />
                  <ReferenceLine
                    y={limite}
                    stroke="#E53E3E"
                    strokeDasharray="5 5"
                    label={{
                      value: `Limite: ${toCurrency(limite)}`,
                      position: 'right',
                      fontSize: 10,
                      fill: '#E53E3E',
                    }}
                  />
                  <Bar
                    dataKey="faturamento"
                    name="Faturamento Mensal"
                    fill="#4299E1"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="acumulado"
                    name="Acumulado"
                    stroke="#38A169"
                    strokeWidth={2}
                    dot={{ fill: '#38A169', strokeWidth: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}

            <Flex justify="center" mt={2} gap={6}>
              <Flex align="center" gap={2}>
                <Box w={3} h={3} bg="blue.400" borderRadius="sm" />
                <Text fontSize="xs" color="gray.600">
                  Faturamento Mensal
                </Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Box w={3} h={3} bg="green.500" borderRadius="full" />
                <Text fontSize="xs" color="gray.600">
                  Acumulado no Ano
                </Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Box w={6} h={0.5} bg="red.500" />
                <Text fontSize="xs" color="gray.600">
                  Limite Anual
                </Text>
              </Flex>
            </Flex>
          </Box>

          {/* Alertas baseados na situação da empresa */}
          {percentualAtingido >= 100 && (
            <Box
              mt={4}
              p={3}
              bg="red.100"
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="red.600"
            >
              <Text fontSize="sm" color="red.800" fontWeight="bold">
                LIMITE EXCEDIDO: A empresa ultrapassou o limite do regime tributário!
              </Text>
              <Text fontSize="xs" color="red.700" mt={1}>
                É necessário avaliar a mudança de regime tributário imediatamente. A empresa pode
                estar sujeita a penalidades e desenquadramento automático.
              </Text>
            </Box>
          )}

          {percentualAtingido >= 80 && percentualAtingido < 100 && (
            <Box
              mt={4}
              p={3}
              bg="orange.50"
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="orange.500"
            >
              <Text fontSize="sm" color="orange.700" fontWeight="medium">
                Atenção: A empresa está próxima de atingir o limite do regime tributário!
              </Text>
              <Text fontSize="xs" color="orange.600" mt={1}>
                Considere avaliar uma mudança de regime para o próximo exercício. Restam apenas{' '}
                {toCurrency(limiteRestante)} do limite anual.
              </Text>
            </Box>
          )}

          {faturamento > 0 && faturamento < 1000 && (
            <Box
              mt={4}
              p={3}
              bg="yellow.50"
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="yellow.500"
            >
              <Text fontSize="sm" color="yellow.700" fontWeight="medium">
                Faturamento muito baixo detectado
              </Text>
              <Text fontSize="xs" color="yellow.600" mt={1}>
                O faturamento acumulado está abaixo de R$ 1.000,00. Verifique se há notas fiscais
                pendentes de lançamento.
              </Text>
            </Box>
          )}

          {(!company.faixa_atual || company.faixa_atual === 'N/A') && (
            <Box
              mt={4}
              p={3}
              bg="purple.50"
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="purple.500"
            >
              <Text fontSize="sm" color="purple.700" fontWeight="medium">
                Regime tributário não identificado
              </Text>
              <Text fontSize="xs" color="purple.600" mt={1}>
                Não foi possível identificar o regime tributário desta empresa. Verifique os dados
                cadastrais.
              </Text>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
